import { api } from './api'

export interface NetUsageResponse {
  organization_id: number
  month: string
  net_records: number
  net_bytes: number
}

export interface TierFeature {
  tier_name: string
  base_volume: number
  cap_volume: number
  price: number
}

export interface SubscriptionInfoResponse {
  organization_id: number
  has_subscription: boolean
  plan_name?: string
  plan_type?: string
  description?: string
  price_monthly?: number
  price_annual?: number
  currency: string
  frequency?: string
  is_active: boolean
  trial_days?: number
  // True when the subscription is currently in a Stripe trial AND the trial
  // hasn't ended yet. Backend computes from subscription.trial AND
  // expiration_date > now (see billing_status.py). Frontend reads this
  // alongside expiration_date to render a "Free trial - N days remaining"
  // badge that auto-disappears when the trial converts.
  is_trialing?: boolean
  start_date?: string
  expiration_date?: string
  tiers: TierFeature[]
  max_cap_volume: number
  current_tier?: TierFeature
  // The tier key stored in the subscription (e.g., "0_10M", "10M_30M")
  subscription_tier?: string
  emails?: string
}

export interface PaymentMethod {
  id: string
  last4: string
  brand: string
  exp_month: number
  exp_year: number
  default: boolean
  created?: number
}

export interface Invoice {
  id: string
  number: string
  status: string
  amount_due: number
  amount_paid: number
  currency: string
  created: number
  period_start: number
  period_end: number
  invoice_url: string | null
  invoice_pdf: string | null
  paid: boolean
}

export interface UsageResponse {
  organization_id: number
  organization_name: string
  current_tier: {
    tier_number: number
    tier_cap: number
    monthly_price: number
    nickname: string
  }
  mtd_records: number
  mtd_backfill_records: number
  last_updated: string
  billing_period: {
    start: string
    end: string
  }
  daily_usage: Array<{
    date: string
    records: number
    tier: Record<string, unknown>
    daily_cost: number
    cumulative_records: number
  }>
  projected_monthly_cost: number
  tier_changes_this_month: number
  // committed-tier billing fields (drive the navbar widget + upgrade dialog)
  mode?: string | null
  committed_tier?: string | null
  committed_tier_price?: number | null
  tier_cap?: number | null
  records_remaining?: number | null
  required_tier?: string | null
  required_tier_price?: number | null
  needs_upgrade?: boolean
  is_free?: boolean
  is_exempt?: boolean
  blocked?: boolean
  block_reason?: string | null
}

export interface UpgradeApproveResponse {
  organization_id: number
  previous_tier?: string | null
  tier: string
  monthly_price: number
  quantity: number
  stripe_subscription_id: string
}

export const billingService = {
  /**
   * Get net usage (records and bytes) for the current month or a specific month
   * @param yearMonth Optional year-month in YYYY-MM format. Defaults to current month.
   * @deprecated Use getUsage() instead - queries ClickHouse directly for accurate MTD records
   */
  async getNetUsage(yearMonth?: string): Promise<NetUsageResponse> {
    const params = yearMonth ? { year_month: yearMonth } : {}
    const response = await api.get<NetUsageResponse>('/api/billing/net-usage', { params })
    return response.data
  },

  /**
   * Get usage data from ClickHouse (MTD records count from cube_{org_id} table)
   * This is the accurate source for billing - queries ClickHouse directly
   * @param yearMonth Optional year-month in YYYY-MM format. Defaults to current month.
   */
  async getUsage(yearMonth?: string): Promise<UsageResponse> {
    const params = yearMonth ? { year_month: yearMonth } : {}
    const response = await api.get<UsageResponse>('/api/billing/usage', { params })
    return response.data
  },

  /**
   * Get subscription information including plan details and tier caps
   */
  async getSubscriptionInfo(): Promise<SubscriptionInfoResponse> {
    const response = await api.get<SubscriptionInfoResponse>('/api/billing/subscription-info')
    return response.data
  },

  /**
   * Get payment methods from Stripe
   */
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    const response = await api.get<PaymentMethod[]>('/stripe/cards')
    return response.data
  },

  /**
   * Set a payment method as the default
   */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`/stripe/${paymentMethodId}/make_preferred`)
    return response.data
  },

  /**
   * Remove a payment method
   */
  async removePaymentMethod(paymentMethodId: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/stripe/${paymentMethodId}/destroy_payment`)
    return response.data
  },

  /**
   * Get invoices from Stripe
   */
  async getInvoices(limit = 24): Promise<Invoice[]> {
    const response = await api.get<Invoice[]>('/stripe/invoices', { params: { limit } })
    return response.data
  },

  /**
   * Update subscription emails
   */
  async updateSubscriptionEmails(emails: string): Promise<{ organization_id: number; emails: string; message: string }> {
    const response = await api.put<{ organization_id: number; emails: string; message: string }>('/api/billing/subscription-emails', { emails })
    return response.data
  },

  /**
   * Update subscription tier (admin only)
   * @param tier The tier key from subscription_plans.features (e.g., "0_10M", "10M_30M")
   */
  async updateSubscriptionTier(tier: string): Promise<{ organization_id: number; tier: string; tier_details?: TierFeature; message: string }> {
    const response = await api.put<{ organization_id: number; tier: string; tier_details?: TierFeature; message: string }>('/api/billing/subscription-tier', { tier })
    return response.data
  },

  /**
   * Start a Stripe Checkout session for subscribing.
   *
   * Used by the onboarding wizard (/get-started/payment) to capture a CC
   * before the org exists, and by Settings → Subscribe Now for orgs that
   * never went through the new onboarding (no stripe_customer_id yet).
   *
   * Returns the Stripe-hosted URL — caller is expected to redirect with
   * window.location.href = url.
   */
  async createCheckoutSession(params: { success_path: string; cancel_path: string }): Promise<{ session_id: string; url: string }> {
    const response = await api.post<{ session_id: string; url: string }>('/api/billing/checkout-session', params)
    return response.data
  },

  /**
   * Create a Stripe Customer Portal session for managing an existing subscription.
   *
   * Used by Settings → Manage Subscription. Requires the org to have a
   * stripe_customer_id; the api returns 400 otherwise (caller should fall
   * back to createCheckoutSession).
   *
   * @param returnPath Frontend path Stripe redirects to after the user closes
   *                   the portal. Use {org_slug} substitution server-side.
   */
  async createPortalSession(returnPath: string): Promise<{ url: string }> {
    const response = await api.post<{ url: string }>('/api/billing/portal-session', { return_path: returnPath })
    return response.data
  },

  /**
   * Approve moving an ALREADY-PAYING org to a higher volume band.
   *
   * Sets the Stripe subscription's quantity to the target band's cap, so the
   * Volume price bills that band's flat fee, and invoices the difference
   * immediately. Orgs with no subscription get a 409 — they must go through
   * createCheckoutSession() to add a payment method first.
   *
   * @param tier Optional target band key. Omit to upgrade to the band the org's
   *             current volume requires.
   */
  async approveUpgrade(tier?: string): Promise<UpgradeApproveResponse> {
    const response = await api.post<UpgradeApproveResponse>('/api/billing/upgrade', { tier: tier ?? null })
    return response.data
  }
}
